import { useMemo, useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';

/**
 * K3.5 (round-5) — BYOK cost dashboard.
 *
 * Reads /v1/billing/usage and renders a rolling-7-day bar chart of
 * cost_usd + per-provider breakdown. Chart is hand-rolled SVG so we
 * don't pull in a charting library (bundle-budget guard).
 *
 * Copy makes it clear: Shakti is BYOK — the customer is billed by
 * their providers, not by Shakti. This dashboard reflects the token
 * cost Shakti observed across its own calls; final authoritative
 * spend lives with the provider.
 */
interface UsageRow {
  bucket: string;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
}

export function CostsRoute(): JSX.Element {
  const [providerRows, setProviderRows] = useState<UsageRow[]>([]);
  const [dayRows, setDayRows] = useState<UsageRow[]>([]);
  const [agentRows, setAgentRows] = useState<UsageRow[]>([]);
  const [totalUsd, setTotalUsd] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const [provider, day, agent] = await Promise.all([
        shaktiApi.getBillingUsage({ ...range, group_by: 'provider' }),
        shaktiApi.getBillingUsage({ ...range, group_by: 'day' }),
        shaktiApi.getBillingUsage({ ...range, group_by: 'agent' }),
      ]);
      setProviderRows(provider.rows);
      setDayRows(day.rows);
      setAgentRows(agent.rows);
      setTotalUsd(provider.total_usd);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  // N5.5 / R5 (round-6) — refetch on project switch + abort prior
  // in-flight requests so stale data from another project can't bleed
  // into this view.
  useProjectScopedEffect(() => {
    void refresh();
  });

  return (
    <section aria-labelledby="costs-title" className="mx-auto flex max-w-6xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/costs
        </span>
        <h1 id="costs-title" className="text-2xl font-semibold text-fg-primary">
          BYOK cost
        </h1>
        <p className="text-sm text-fg-secondary">
          Shakti is BYOK — you are billed by your LLM providers directly. Numbers here reflect
          the token cost Shakti observed across its own calls in the last 7 days. Final
          authoritative spend lives with the provider.
        </p>
      </header>

      {error ? (
        <div
          role="alert"
          className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
        >
          {error}
        </div>
      ) : null}

      <section className="shakti-card flex items-center justify-between p-6" aria-label="Total">
        <div>
          <span className="text-2xs uppercase tracking-wider text-fg-muted">
            Last 7 days · all providers
          </span>
          <div className="mt-1 text-3xl font-semibold">${totalUsd.toFixed(4)}</div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </section>

      <section className="shakti-card p-6" aria-label="Daily spend">
        <h2 className="mb-3 text-lg font-semibold">Daily spend</h2>
        {dayRows.length === 0 ? (
          <p className="text-xs text-fg-muted">No usage recorded yet.</p>
        ) : (
          <DailyBarChart rows={dayRows} />
        )}
      </section>

      <section className="shakti-card p-6" aria-label="By provider">
        <h2 className="mb-3 text-lg font-semibold">By provider</h2>
        <UsageTable rows={providerRows} emptyMessage="No provider-scoped usage recorded yet." />
      </section>

      <section className="shakti-card p-6" aria-label="By agent">
        <h2 className="mb-3 text-lg font-semibold">By agent</h2>
        <UsageTable rows={agentRows} emptyMessage="No agent-scoped usage recorded yet." />
      </section>
    </section>
  );
}

function UsageTable({ rows, emptyMessage }: { rows: UsageRow[]; emptyMessage: string }): JSX.Element {
  if (rows.length === 0) {
    return <p className="text-xs text-fg-muted">{emptyMessage}</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-2xs uppercase tracking-wider text-fg-muted">
          <th className="py-2">Bucket</th>
          <th>Tokens in</th>
          <th>Tokens out</th>
          <th className="text-right">Cost (USD)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.bucket} className="border-t border-border-subtle">
            <td className="py-2 shakti-mono">{r.bucket}</td>
            <td>{r.tokens_in.toLocaleString()}</td>
            <td>{r.tokens_out.toLocaleString()}</td>
            <td className="text-right font-semibold">${r.cost_usd.toFixed(4)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DailyBarChart({ rows }: { rows: UsageRow[] }): JSX.Element {
  const max = Math.max(0.001, ...rows.map((r) => r.cost_usd));
  const width = 640;
  const height = 140;
  const gap = 6;
  const barWidth = Math.max(6, (width - gap * (rows.length - 1)) / Math.max(1, rows.length));

  return (
    <svg
      viewBox={`0 0 ${width} ${height + 20}`}
      className="w-full"
      role="img"
      aria-label="Daily spend bar chart"
    >
      {rows.map((r, i) => {
        const h = Math.max(1, (r.cost_usd / max) * height);
        const x = i * (barWidth + gap);
        const y = height - h;
        return (
          <g key={r.bucket}>
            <title>{`${r.bucket}: $${r.cost_usd.toFixed(4)}`}</title>
            <rect x={x} y={y} width={barWidth} height={h} fill="#5B7EF8" rx={2} ry={2} />
            <text
              x={x + barWidth / 2}
              y={height + 14}
              fontSize="10"
              fill="#6F7989"
              textAnchor="middle"
              className="shakti-mono"
            >
              {r.bucket.slice(5)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.body?.error ?? e.message} (${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}
